/**
 * The model behind "Delete the drops outside the window" (19/10/2026): the
 * draft timed — with the plan chain, like the General tab — on today's run
 * date and on every day of the next year a Danish schedule can fall
 * differently (`timingDays`: around each clock change), read by the pure
 * `outsideRemoval`. Null when the container lifts the window (nothing is
 * "outside — not sent" there). For leading drops — or a delete that would
 * leave no drop on — it also says what "Start at window open" would leave
 * outside (never offered on a route test: its first drop must be before the
 * window).
 * For Media Devoted | Created By Ciro Autiero
 */
import { ROUTE_TEST_KIND } from '@trafficpulse/shared';
import { useMemo } from 'react';

import type { ContainerDraft, DropGenPlanDetail } from '../../../../drop-generator.types';
import { timingDays } from '../../../../utils/clock/clock-change-days';
import { planDay } from '../../../../utils/clock/plan-day';
import { outsideRemoval, type OutsideRemoval, type RemovalDay } from '../../../../utils/drops/outside-removal';
import type { ContainerTimes } from '../../../../utils/slot-times';
import { useCountryClock } from '../../../shared/time/country-clock-context';
import { chainTimes, type TimingDraft, timingOf } from './chain-times';

export interface OutsideRemovalModel extends OutsideRemoval {
  /** Drops outside the window today that will not go — the button shows only when > 0. */
  outsideToday: number;
  /** The country's window (country minutes). */
  window: { startMin: number; endMin: number };
  /** The delete takes every drop that is on — the container would send nothing. */
  leavesNone: boolean;
  /** "Start at window open" for the leading drops (or instead of emptying it), and how many drops it would leave outside today. */
  startFix: { outsideAfter: number } | null;
}

export const WINDOW_OPEN = 'WINDOW_OPEN';

const countOutside = (times: ContainerTimes | undefined): number => (times?.slotTimes ?? []).filter((t) => t.outsideWindow === true).length;

export function useOutsideRemoval(draft: ContainerDraft, plan: DropGenPlanDetail, containerId: string | null): OutsideRemovalModel | null {
  const { runDate } = useCountryClock();
  const today = planDay(plan, runDate).runDate;
  const days = useMemo(() => timingDays(plan.recipientTimezone, today), [plan.recipientTimezone, today]);
  // Re-timed only when a timing field changes — typing a name or an amount never re-times the year.
  const timing = JSON.stringify(timingOf(draft));
  return useMemo(
    () => outsideRemovalModel(JSON.parse(timing) as TimingDraft, plan, containerId, days),
    [timing, plan, containerId, days],
  );
}

/** Pure: the model for the run dates given — today first, then the other days to check, in date order. */
export function outsideRemovalModel(
  draft: TimingDraft,
  plan: DropGenPlanDetail,
  containerId: string | null,
  runDates: string[],
): OutsideRemovalModel | null {
  if (draft.sendOutsideWindow === true) return null;
  const timed = runDates.map((runDate) => chainTimes(draft, plan, containerId, runDate).times);
  const days: RemovalDay[] = timed.map((t, k) => ({ runDate: runDates[k], times: new Map((t?.slotTimes ?? []).map((s) => [s.slotId ?? '', s])) }));
  const routeTest = draft.kind === ROUTE_TEST_KIND;
  const removal = outsideRemoval(draft.slots, days, { routeTest, windowStartMin: plan.windowStartMin });
  const leavesNone = removal.remove.length > 0 && removal.remove.length === draft.slots.filter((s) => s.enabled).length;
  const fixable = (removal.leading > 0 || leavesNone) && !routeTest && draft.startMode !== WINDOW_OPEN;
  const fixed = fixable ? chainTimes({ ...draft, startMode: WINDOW_OPEN }, plan, containerId, runDates[0]).times : undefined;
  return {
    ...removal,
    outsideToday: countOutside(timed[0]),
    leavesNone,
    window: { startMin: plan.windowStartMin, endMin: plan.windowEndMin },
    startFix: fixable ? { outsideAfter: countOutside(fixed) } : null,
  };
}
